import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { useAuth } from "@/context/auth/auth-context"
import { useCurrency } from "@/context/currency/currency-context"
import hive, { triggerDownload, getCashBalance } from "@/lib/api/hive"
import { type Portfolio, type Position, type DividendEvent, type SyncStatus, type CashBalance } from "@/types/portfolio"
import { type Stock } from "@/types/stock"
import { calculatePortfolioStats, computeSafetyDistribution, mergeAggregatePositions } from "@/lib/portfolio-math"
import { hydratePosition, positionToStock } from "@/lib/portfolio-utils"
import { calculateIncomeSummary } from "@/lib/forecast-helper"
import { DashboardHeader } from "@/components/custom/dashboard/dashboard-header"
import { PortfolioSummary } from "@/components/custom/dashboard/portfolio-summary"
import { PortfolioPieChart } from "@/components/custom/dashboard/portfolio-pie-chart"
import { PositionsTable } from "@/components/custom/dashboard/positions-table"
import { TransactionForm } from "@/components/custom/dashboard/transaction-form"
import { CashLedgerPanel } from "@/components/custom/dashboard/cash-ledger"
import { StockDetailSheet } from "@/components/custom/dashboard/stock-detail-sheet"
import { CreatePortfolioDialog } from "@/components/custom/dashboard/create-portfolio-dialog"
import { DeletePortfolioDialog } from "@/components/custom/dashboard/delete-portfolio-dialog"
import { DividendBarChart } from "@/components/custom/dashboard/dividend-bar-chart.tsx"
import { ActivityFeed } from "@/components/custom/dashboard/activity-feed"
import { PerformanceSection } from "@/components/custom/dashboard/performance-section"
import { Loader2, ChevronLeft, LayoutGrid, CalendarDays, Download, Coins, Settings, Activity, AlertTriangle } from "lucide-react"
import { FireSummaryWidget } from "@/components/custom/dashboard/fire-summary-widget"
import { PortfolioSettingsPanel } from "@/components/custom/dashboard/portfolio-settings-panel"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { StatsCardSkeleton, IncomeSummaryCardSkeleton } from "@/components/custom/skeletons"
import { cn } from "@/lib/utils"
import { Analytics } from "@/lib/analytics"
import { AIChatDrawer } from "@/components/custom/dashboard/ai-chat-drawer"

function sanitizeFilename(name: string): string {
	return name.replace(/[^a-zA-Z0-9_-]/g, "_") || "portfolio"
}

// Fallback for positions whose background sync hasn't completed yet.
// Returns a minimal Position with placeholder stock fields so the position
// is visible in the dashboard immediately after import.
function toMinimalPosition(p: Position): Position {
	return {
		...p,
		name: p.ticker,
		price: 0,
		marketCap: "0",
		sector: "Unknown",
		dividendPerShare: 0,
		dividendYield: 0,
		dividendGrowth5y: 0,
		frequency: "Quarterly" as const,
		currency: p.currency || "USD",
	}
}

export default function DashboardPage() {
	const { user, token, isAuthenticated } = useAuth()
	const { format, convert, currency } = useCurrency()
	const navigate = useNavigate()
	const [portfolios, setPortfolios] = useState<Portfolio[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const { portfolioId } = useParams<{ portfolioId?: string }>()
	const selectedPortfolioId = portfolioId ?? null
	const [stockCache, setStockCache] = useState<Record<string, Stock>>({})
	const [selectedStock, setSelectedStock] = useState<{
		stock: Stock
		totalShares: number
		averagePrice: number
		totalCost: number
	} | null>(null)
	const [detailPositions, setDetailPositions] = useState<Position[] | null>(null)
	const [allDetailPositions, setAllDetailPositions] = useState<Position[] | null>(null)
	const [totalDividendsReceived, setTotalDividendsReceived] = useState<number>(0)
	const [allPortfoliosDividendsReceived, setAllPortfoliosDividendsReceived] = useState<number>(0)
	const [forecastEvents, setForecastEvents] = useState<DividendEvent[]>([])
	const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
	const [portfolioRefreshKey, setPortfolioRefreshKey] = useState(0)
	const [showPortfolioSettings, setShowPortfolioSettings] = useState(false)
	const [feedOpen, setFeedOpen] = useState(false)
	const [stocksHydrated, setStocksHydrated] = useState(false)
	const [dividendSummaryLoading, setDividendSummaryLoading] = useState(true)
	const [forecastLoaded, setForecastLoaded] = useState(false)
	const [cashBalance, setCashBalance] = useState<CashBalance | null>(null)
	const [cashBalanceError, setCashBalanceError] = useState<string | null>(null)
	const [cashByPortfolio, setCashByPortfolio] = useState<Map<string, CashBalance>>(new Map())
	const [cashRefreshKey, setCashRefreshKey] = useState(0)
	// Safety scores — disabled until EODHD fundamentals plan is active
	const [positionSafetyMap] = useState<Map<string, { score: number; rating: string }>>(new Map())

	// Tracks which ticker:exchange keys have already gone through self-heal — prevents
	// re-firing addTicker + getBulkStocks on every fetchPortfolios call for persistent
	// zero-price stocks (delisted, provider gaps, sync not yet propagated).
	const healAttemptedRef = useRef<Set<string>>(new Set())
	// Ref mirror of stockCache so fetchPortfolios can read current cache without
	// being listed as a dependency (which would recreate the callback on every update).
	const stockCacheRef = useRef(stockCache)
	stockCacheRef.current = stockCache

	const portfolioVersionKey = useMemo(() => portfolios.map((p) => `${p.id}:${p.updated_at}`).join(","), [portfolios])

	const fetchPortfolios = useCallback(
		async (silent = false) => {
			if (!isAuthenticated || !token || !user) return

			try {
				if (!silent) setIsLoading(true)
				const data = await hive.getPortfolios(token, user.id)

				// Hydrate unique tickers using bulk API — fetch all missing stock data
				// before touching state so both updates land in the same React batch.
				// This prevents the detailPositions effect from running with a stale
				// stockCache when a brand-new holding is added.
				const allPositions = data.flatMap((p) => p.positions || [])
				const missingPositions = allPositions.filter((p) => {
					const cached = stockCacheRef.current[`${p.ticker}:${p.exchange}`]
					return !cached || cached.price === 0
				})

				const newStockData: Record<string, Stock> = {}
				if (missingPositions.length > 0) {
					const uniqueMissing = Array.from(
						new Map(
							missingPositions.map((p) => [
								`${p.ticker}:${p.exchange}`,
								{ symbol: p.ticker, exchange: p.exchange },
							]),
						).values(),
					)
					const stocks = await hive.getBulkStocks(uniqueMissing)
					stocks.forEach((stock) => {
						newStockData[`${stock.ticker}:${stock.exchange}`] = stock
					})

					// Any stocks that still have price=0 after the bulk fetch have a
					// persistent data gap in the backend DB. Trigger SyncTicker for each
					// one (via POST /v1/tickers) so the backend re-fetches from all
					// fallback sources (EODHD EOD → Finnhub). After the syncs complete,
					// re-fetch those specific stocks and patch the cache.
					const stillZero = stocks.filter(
						(s) => s.price === 0 && !healAttemptedRef.current.has(`${s.ticker}:${s.exchange}`),
					)
					stillZero.forEach((s) => healAttemptedRef.current.add(`${s.ticker}:${s.exchange}`))
					if (stillZero.length > 0 && token) {
						Promise.all(
							stillZero.map((s) => hive.addTicker(s.ticker, s.exchange, token).catch(() => null)),
						).then(async () => {
							try {
								const resynced = await hive.getBulkStocks(
									stillZero.map((s) => ({ symbol: s.ticker, exchange: s.exchange })),
								)
								// Only patch cache for stocks the heal actually fixed.
								// Avoids a portfolioRefreshKey bump (and its downstream cascade)
								// for stocks that remain at zero after the heal attempt.
								const fixed = resynced.filter((s) => s.price > 0)
								if (fixed.length > 0) {
									const patched: Record<string, Stock> = {}
									fixed.forEach((s) => {
										patched[`${s.ticker}:${s.exchange}`] = s
									})
									setStockCache((prev) => ({ ...prev, ...patched }))
								}
							} catch {
								/* best-effort */
							}
						})
					}
				}

				// Batch all updates synchronously so React renders once with consistent
				// state: stockCache first (so detailPositions effect sees the new ticker),
				// then portfolios, then bump the refresh key to guarantee the
				// detailPositions effect re-runs even if updated_at didn't change.
				if (Object.keys(newStockData).length > 0) {
					setStockCache((prev) => ({ ...prev, ...newStockData }))
				}
				setPortfolios(data)
				setPortfolioRefreshKey((k) => k + 1)
				setStocksHydrated(true)
			} catch (err: unknown) {
				setError(err instanceof Error ? err.message : "Failed to fetch portfolios")
			} finally {
				if (!silent) setIsLoading(false)
			}
		},
		[isAuthenticated, token, user],
	)

	useEffect(() => {
		fetchPortfolios()
	}, [isAuthenticated, token, user]) // Simplified dependency array to avoid infinite loop with stockCache if not careful

	// Fetch data freshness status, poll every 30s so badge updates as background workers finish
	useEffect(() => {
		if (!isAuthenticated || !token) return
		const fetch = () =>
			hive
				.getSyncStatus(token)
				.then(setSyncStatus)
				.catch(() => {})
		fetch()
		const interval = setInterval(fetch, 30_000)
		return () => clearInterval(interval)
	}, [isAuthenticated, token])

	// Fetch detail-view data when a portfolio is selected (Task 2: Step 6)
	useEffect(() => {
		if (!selectedPortfolioId || !token) {
			setDetailPositions(null)
			setAllDetailPositions(null)
			return
		}
		let cancelled = false
		const fetchDetail = async () => {
			try {
				const detail = await hive.getPortfolios(token, undefined, selectedPortfolioId)
				if (cancelled) return
				if (detail.length > 0) {
					const enriched = (detail[0].positions || []).map((p) => {
						const stock = stockCache[`${p.ticker}:${p.exchange}`]
						if (stock) return hydratePosition(p, stock)
						return toMinimalPosition(p)
					})
					setAllDetailPositions(enriched)
					setDetailPositions(enriched.filter((p): p is Position => p.shares > 0))
				}
			} catch (err) {
				console.error("Failed to fetch detail positions:", err)
			}
		}
		fetchDetail()
		return () => {
			cancelled = true
		}
	}, [selectedPortfolioId, token, portfolioRefreshKey])

	// Fetch cash balance when a single portfolio is selected
	const refreshCashBalance = useCallback(async () => {
		if (!selectedPortfolioId) {
			setCashBalance(null)
			setCashBalanceError(null)
			return
		}
		try {
			const balance = await getCashBalance(selectedPortfolioId)
			setCashBalance(balance)
			setCashBalanceError(null)
		} catch (err) {
			setCashBalance(null)
			setCashBalanceError(err instanceof Error ? err.message : "Cash balance unavailable")
		}
		setCashRefreshKey((k) => k + 1)
	}, [selectedPortfolioId])

	useEffect(() => {
		refreshCashBalance()
	}, [selectedPortfolioId])


	// Fetch cash balances for all cash-enabled portfolios (aggregate view)
	useEffect(() => {
		if (!token || portfolios.length === 0 || selectedPortfolioId) return
		let cancelled = false
		const fetchAll = async () => {
			const enabled = portfolios.filter((p) => p.cash_tracking_enabled)
			if (enabled.length === 0) {
				setCashByPortfolio(new Map())
				return
			}
			try {
				const results = await Promise.all(enabled.map((p) => getCashBalance(p.id).catch(() => null)))
				if (cancelled) return
				const map = new Map<string, CashBalance>()
				results.forEach((bal, i) => {
					if (bal) map.set(enabled[i].id, bal)
				})
				setCashByPortfolio(map)
			} catch {
				// fail silently
			}
		}
		fetchAll()
		return () => {
			cancelled = true
		}
	}, [token, portfolioVersionKey, selectedPortfolioId])

	// Compute total dividends received from detail positions (Task 3: D14 single-portfolio)
	// Convert each position's dividends from its native currency to display currency
	useEffect(() => {
		if (allDetailPositions) {
			const sum = allDetailPositions.reduce(
				(acc, p) => acc + convert(p.total_dividends_received || 0, p.currency),
				0,
			)
			setTotalDividendsReceived(sum)
		} else {
			setTotalDividendsReceived(0)
		}
	}, [allDetailPositions, convert])

	// Fetch dividend summaries for all portfolios (Task 3: D14 all-portfolios)
	useEffect(() => {
		if (!token || portfolios.length === 0) {
			setAllPortfoliosDividendsReceived(0)
			return
		}
		let cancelled = false
		const fetchSummaries = async () => {
			setDividendSummaryLoading(true)
			try {
				const summaries = await Promise.all(portfolios.map((p) => hive.getDividendSummary(p.id, token)))
				if (cancelled) return
				// Convert each position's dividends from its native currency to display currency
				const total = summaries.reduce(
					(acc, s) =>
						acc + s.by_position.reduce((sum, bp) => sum + convert(bp.total_received, bp.currency), 0),
					0,
				)
				setAllPortfoliosDividendsReceived(total)
			} catch (err) {
				console.error("Failed to fetch dividend summaries:", err)
			} finally {
				if (!cancelled) setDividendSummaryLoading(false)
			}
		}
		fetchSummaries()
		return () => {
			cancelled = true
		}
	}, [token, portfolioVersionKey, convert])

	const hydratedPortfolios = useMemo(() => {
		return portfolios.map((portfolio) => {
			const positions: Position[] = (portfolio.positions || [])
				.map((p) => {
					const stock = stockCache[`${p.ticker}:${p.exchange}`]
					if (!stock) return toMinimalPosition(p)
					return hydratePosition(p, stock)
				})
				.filter((p): p is Position => p.shares > 0)

			const stats = calculatePortfolioStats(positions, convert)

			return {
				...portfolio,
				hydratedPositions: positions,
				stats,
			}
		})
	}, [portfolios, stockCache, convert])

	const aggregateStats = useMemo(() => {
		if (hydratedPortfolios.length === 0) return null

		const allPositions = mergeAggregatePositions(
			hydratedPortfolios.flatMap((portfolio) => portfolio.hydratedPositions),
		)

		const stats = calculatePortfolioStats(allPositions, convert)

		return {
			...stats,
			allPositions,
		}
	}, [hydratedPortfolios, convert])

	const aggregateCash = useMemo(() => {
		if (cashByPortfolio.size === 0) return null
		let total = 0
		cashByPortfolio.forEach((bal) => {
			total += convert(bal.balance, bal.currency)
		})
		return { total, anyEnabled: true }
	}, [cashByPortfolio, convert])

	const selectedPortfolio = useMemo(() => {
		return hydratedPortfolios.find((p) => p.id === selectedPortfolioId)
	}, [hydratedPortfolios, selectedPortfolioId])

	const safetyDistribution = useMemo(() => {
		if (!selectedPortfolio) return undefined
		return computeSafetyDistribution(selectedPortfolio.hydratedPositions ?? [], positionSafetyMap)
	}, [selectedPortfolio, positionSafetyMap])

	const allPortfolioIds = useMemo(() => portfolios.map((p) => p.id), [portfolioVersionKey])

	const selectedPortfolioIds = useMemo(
		() => (selectedPortfolio ? [selectedPortfolio.id] : []),
		[selectedPortfolio?.id, selectedPortfolio?.updated_at],
	)

	const allActiveTickers = useMemo(() => aggregateStats?.allPositions.map((p) => p.ticker) || [], [aggregateStats])

	const selectedActiveTickers = useMemo(
		() => selectedPortfolio?.hydratedPositions.map((p) => p.ticker) || [],
		[selectedPortfolio?.hydratedPositions],
	)

	const incomeSummary = useMemo(() => calculateIncomeSummary(forecastEvents, convert), [forecastEvents, convert])

	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
				<Loader2 className="h-8 w-8 animate-spin text-sky-600" />
				<p className="text-muted-foreground animate-pulse">Gathering nectar from your hives...</p>
			</div>
		)
	}

	if (error) {
		return (
			<div className="container mx-auto px-4 py-12 text-center">
				<h2 className="text-2xl font-bold text-red-600 mb-2">Oops! Something went wrong</h2>
				<p className="text-muted-foreground mb-6">{error}</p>
				<Button onClick={() => window.location.reload()} variant="outline">
					Try Again
				</Button>
			</div>
		)
	}

	if (hydratedPortfolios.length === 0) {
		return (
			<div className="container mx-auto px-4 py-12 text-center max-w-2xl">
				<h2 className="text-3xl font-bold text-sky-950 dark:text-sky-50 mb-4">Your Hive is Empty</h2>
				<p className="text-muted-foreground mb-8">
					You haven't saved any portfolios yet. You can start with a fresh one right here, or try the demo to
					see how it works!
				</p>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<CreatePortfolioDialog onSuccess={fetchPortfolios} />
					<Button
						onClick={() => navigate("/demo")}
						variant="outline"
						className="h-auto py-8 border-2 border-dashed border-sky-200 dark:border-sky-900/50 rounded-xl flex flex-col gap-2 hover:bg-sky-50/50 dark:hover:bg-sky-900/10"
					>
						<span className="font-bold text-sky-900 dark:text-sky-100 text-lg">Try Demo</span>
						<span className="text-xs text-muted-foreground">Experiment with pre-filled stocks</span>
					</Button>
				</div>
			</div>
		)
	}

	if (selectedPortfolio) {
		const displayPositions = detailPositions ?? selectedPortfolio.hydratedPositions
		const portfolioSummaryCard = (
			<PortfolioSummary
				totalValue={selectedPortfolio.stats.totalValue}
				annualIncome={selectedPortfolio.stats.annualIncome}
				portfolioYield={selectedPortfolio.stats.portfolioYield}
				avgDividendGrowth={selectedPortfolio.stats.avgDividendGrowth}
				yieldOnCost={selectedPortfolio.stats.yieldOnCost}
				safetyDistribution={safetyDistribution}
			/>
		)

		return (
			<>
				<div className="w-full max-w-[1400px] mx-auto px-4 py-8">
					<div className="lg:flex lg:gap-8">
						<div className="flex-1 min-w-0 space-y-8">
							<div className="flex items-center justify-between mb-2">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => navigate("/dashboard")}
									className="hover:bg-sky-100 dark:hover:bg-sky-900/30"
								>
									<ChevronLeft className="h-4 w-4 mr-1" />
									Back to All Hives
								</Button>
								<div className="flex items-center gap-2">
									<AIChatDrawer
										portfolioId={selectedPortfolio.id}
										portfolioName={selectedPortfolio.name}
										token={token!}
										onTransactionAdded={() => {
											fetchPortfolios(true)
											refreshCashBalance()
										}}
									/>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="ghost"
												size="sm"
												className="hover:bg-sky-100 dark:hover:bg-sky-900/30"
												onClick={async () => {
													if (!token) return
													try {
														const blob = await hive.downloadTransactionsCSV(
															selectedPortfolio.id,
															token,
														)
														triggerDownload(
															blob,
															`beediv-transactions-${sanitizeFilename(selectedPortfolio.name)}.csv`,
														)
														Analytics.capture("Export CSV")
													} catch {
														// silent fail — could add toast later
													}
												}}
											>
												<Download className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>Export transaction history</TooltipContent>
									</Tooltip>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setShowPortfolioSettings((s) => !s)}
										className="hover:bg-sky-100 dark:hover:bg-sky-900/30"
									>
										<Settings className="h-4 w-4" />
									</Button>
									<DeletePortfolioDialog
										portfolioId={selectedPortfolio.id}
										portfolioName={selectedPortfolio.name}
										positionCount={selectedPortfolio.hydratedPositions.length}
										token={token!}
										onDeleted={() => {
											navigate("/dashboard")
											fetchPortfolios(true)
										}}
									/>
								</div>
							</div>

							<DashboardHeader
								totalValue={selectedPortfolio.stats.totalValue}
								title={selectedPortfolio.name}
								subtitle={`Managed portfolio with ${selectedPortfolio.hydratedPositions.length} honeycombs`}
								loading={!stocksHydrated}
								onTitleEdit={async (newName) => {
									await hive.updatePortfolio(selectedPortfolio.id, { name: newName }, token!)
									await fetchPortfolios(true)
									Analytics.capture("Portfolio Renamed")
								}}
								cashBalance={cashBalance?.balance}
								cashCurrency={cashBalance?.currency}
								cashEnabled={cashBalance?.enabled}
								cashError={cashBalanceError ?? undefined}
							/>

							{showPortfolioSettings && (
								<PortfolioSettingsPanel
									portfolio={selectedPortfolio}
									onUpdated={() => {
										fetchPortfolios(true)
									}}
									onCashToggled={refreshCashBalance}
								/>
							)}

							<div className="lg:hidden">{portfolioSummaryCard}</div>

							<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
								<div className="lg:col-span-2 space-y-8">
									<TransactionForm
										portfolioId={selectedPortfolio.id}
										onTransactionAdded={() => {
											fetchPortfolios(true)
											refreshCashBalance()
										}}
										token={token!}
										cashBalance={cashBalance?.balance}
										cashCurrency={cashBalance?.currency}
										cashEnabled={cashBalance?.enabled}
										onCashChange={refreshCashBalance}
									/>
									{cashBalance?.enabled ? (
										<CashLedgerPanel
											portfolioId={selectedPortfolio.id}
											currency={cashBalance.currency}
											cashBalance={cashBalance.balance}
											onEntryDeleted={refreshCashBalance}
											refreshKey={cashRefreshKey}
										/>
									) : (
										selectedPortfolio.cash_tracking_enabled && cashBalanceError && (
											<div className="flex items-center gap-3 p-4 bg-amber-50/50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800/30">
												<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
												<div>
													<p className="text-sm font-medium text-amber-700 dark:text-amber-300">
														Cash balance unavailable
													</p>
													<p className="text-xs text-muted-foreground">
														A holding uses a currency we can't convert yet. Cash balance and
														activity will appear once exchange rate data is available.
													</p>
												</div>
											</div>
										)
									)}

									{totalDividendsReceived > 0 && (
										<div className="flex items-center gap-3 p-4 bg-sky-50/50 dark:bg-sky-900/20 rounded-xl border border-sky-100 dark:border-sky-900/30">
											<Coins className="w-5 h-5 text-sky-600 dark:text-sky-400" />
											<div>
												<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
													Total Dividends Received
												</p>
												<p className="text-lg font-bold text-sky-900 dark:text-sky-100">
													{format(totalDividendsReceived, { skipConversion: true })}
												</p>
											</div>
										</div>
									)}
								</div>

								<div className="lg:col-span-1">
									<div className="h-[400px]">
										<PortfolioPieChart positions={selectedPortfolio.hydratedPositions} />
									</div>
								</div>
							</div>

							<div className="relative">
								{selectedPortfolioId && !detailPositions && (
									<div className="absolute top-2 right-2 z-10">
										<Loader2 className="h-4 w-4 animate-spin text-sky-500" />
									</div>
								)}
								<PositionsTable
									positions={displayPositions}
									portfolio={selectedPortfolio}
									onRowClick={(position) => {
										setSelectedStock({
											stock: positionToStock(position),
											totalShares: position.shares,
											averagePrice: position.average_price || 0,
											totalCost: position.cost_basis || 0,
										})
									}}
								/>
							</div>

							<PerformanceSection portfolioId={selectedPortfolioId ?? undefined} />

							<div className="mt-8 space-y-3">
								<DividendBarChart
									portfolioIds={selectedPortfolioIds}
									activeTickers={selectedActiveTickers}
									onEventsLoaded={(events) => {
										setForecastEvents(events)
										setForecastLoaded(true)
									}}
								/>

								{!forecastLoaded ? (
									<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
										<IncomeSummaryCardSkeleton />
										<IncomeSummaryCardSkeleton />
										<IncomeSummaryCardSkeleton />
										<IncomeSummaryCardSkeleton />
									</div>
								) : (
									forecastEvents.length > 0 && (
										<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
											<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
												<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
													This Month
												</p>
												<p className="text-lg font-bold text-sky-900 dark:text-sky-100 mt-1">
													{format(incomeSummary.thisMonth, { skipConversion: true })}
												</p>
											</div>
											<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
												<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
													Year-to-Date
												</p>
												<p className="text-lg font-bold text-sky-900 dark:text-sky-100 mt-1">
													{format(incomeSummary.yearToDate, { skipConversion: true })}
												</p>
											</div>
											<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
												<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
													Last Year
												</p>
												<p className="text-lg font-bold text-sky-900 dark:text-sky-100 mt-1">
													{format(incomeSummary.lastYearTotal, { skipConversion: true })}
												</p>
											</div>
											<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
												<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
													YoY Growth
												</p>
												<p
													className={cn(
														"text-lg font-bold mt-1",
														incomeSummary.yoyGrowth != null && incomeSummary.yoyGrowth > 0
															? "text-green-600 dark:text-green-400"
															: incomeSummary.yoyGrowth != null &&
																  incomeSummary.yoyGrowth < 0
																? "text-red-600 dark:text-red-400"
																: "text-muted-foreground",
													)}
												>
													{incomeSummary.yoyGrowth != null
														? `${incomeSummary.yoyGrowth > 0 ? "+" : ""}${incomeSummary.yoyGrowth.toFixed(1)}%`
														: "\u2014"}
												</p>
											</div>
										</div>
									)
								)}

								<div className="flex justify-end">
									<Link to="/calendar">
										<Button
											variant="outline"
											size="sm"
											className="gap-2 text-sky-900 dark:text-sky-100"
										>
											<CalendarDays className="h-4 w-4" />
											View Calendar
										</Button>
									</Link>
								</div>
							</div>

							{selectedStock && (
								<StockDetailSheet
									isOpen={!!selectedStock}
									onClose={() => setSelectedStock(null)}
									stock={selectedStock.stock}
									portfolioId={selectedPortfolio.id}
									token={token!}
									totalShares={selectedStock.totalShares}
									averagePrice={selectedStock.averagePrice}
									totalCost={selectedStock.totalCost}
									onTransactionChanged={() => fetchPortfolios(true)}
									portfolio={selectedPortfolio}
								/>
							)}
						</div>

						{/* Desktop sidebar */}
						<aside className="hidden lg:block w-80 shrink-0">
							<div className="sticky top-20 space-y-4 pb-4 flex flex-col max-h-[calc(100vh-10rem)]">
								<div className="shrink-0">{portfolioSummaryCard}</div>
								<div className="flex-1 min-h-0 overflow-y-auto">
									<ActivityFeed portfolioId={selectedPortfolioId ?? undefined} />
								</div>
							</div>
						</aside>
					</div>
				</div>

				{/* Mobile feed trigger */}
				<div className="lg:hidden fixed bottom-6 right-6 z-50">
					<Sheet open={feedOpen} onOpenChange={setFeedOpen}>
						<SheetTrigger asChild>
							<Button
								size="icon"
								className="rounded-full h-12 w-12 shadow-lg bg-sky-600 hover:bg-sky-700 text-white"
							>
								<Activity className="h-5 w-5" />
							</Button>
						</SheetTrigger>
						<SheetContent className="w-80 p-0 overflow-y-auto">
							<ActivityFeed portfolioId={selectedPortfolioId ?? undefined} />
						</SheetContent>
					</Sheet>
				</div>
			</>
		)
	}

	const aggregatePortfolioSummaryCard = (
		<PortfolioSummary
			totalValue={aggregateStats?.totalValue || 0}
			annualIncome={aggregateStats?.annualIncome || 0}
			portfolioYield={aggregateStats?.portfolioYield || 0}
			avgDividendGrowth={aggregateStats?.avgDividendGrowth || 0}
			yieldOnCost={aggregateStats?.yieldOnCost || 0}
		/>
	)

	return (
		<div className="w-full max-w-[1400px] mx-auto px-4 py-8">
			<div className="lg:flex lg:gap-8">
				<div className="flex-1 min-w-0 space-y-8">
					<DashboardHeader
						totalValue={aggregateStats?.totalValue || 0}
						title="Total Hive"
						subtitle="Aggregate view of all your honeycombs and their growth."
						loading={!stocksHydrated}
						cashBalance={aggregateCash?.anyEnabled ? aggregateCash.total : undefined}
						cashCurrency={aggregateCash?.anyEnabled ? currency.code : undefined}
						cashEnabled={aggregateCash?.anyEnabled}
					/>

					{/* Data Freshness Indicator */}
					{syncStatus &&
						syncStatus.total_tickers > 0 &&
						(() => {
							const freshCount = syncStatus.total_tickers - syncStatus.stale_tickers
							const dotColor =
								syncStatus.stale_tickers === 0
									? "bg-emerald-500"
									: syncStatus.stale_tickers === syncStatus.total_tickers
										? "bg-red-500"
										: "bg-amber-500"
							const hoursAgo = syncStatus.newest_sync_at
								? Math.round((Date.now() - new Date(syncStatus.newest_sync_at).getTime()) / 3600000)
								: null

							return (
								<div
									className="flex items-center gap-2"
									title={`${freshCount} ticker${freshCount !== 1 ? "s" : ""} up to date, ${syncStatus.stale_tickers} stale`}
								>
									<div className={cn("w-2 h-2 rounded-full", dotColor)} />
									<span className="text-xs text-muted-foreground">
										Data as of {hoursAgo != null && hoursAgo >= 1 ? `${hoursAgo}h ago` : "just now"}
									</span>
								</div>
							)
						})()}

					{/* Combined View Stats */}
					{!stocksHydrated ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
							<StatsCardSkeleton />
							<StatsCardSkeleton />
							<StatsCardSkeleton />
							<StatsCardSkeleton />
						</div>
					) : (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
							<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
								<CardHeader className="pb-2">
									<CardDescription>Total Yield</CardDescription>
									<CardTitle className="text-2xl text-sky-600 dark:text-sky-400">
										{(aggregateStats?.portfolioYield || 0).toFixed(2)}%
									</CardTitle>
								</CardHeader>
							</Card>
							<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
								<CardHeader className="pb-2">
									<CardDescription>Annual Income</CardDescription>
									<CardTitle className="text-2xl text-sky-600 dark:text-sky-400">
										{format(aggregateStats?.annualIncome || 0, { skipConversion: true })}
									</CardTitle>
								</CardHeader>
							</Card>
							<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
								<CardHeader className="pb-2">
									<CardDescription>Avg. Div. Growth (5y)</CardDescription>
									<CardTitle
										className={cn(
											"text-2xl",
											(aggregateStats?.avgDividendGrowth || 0) > 0
												? "text-sky-600 dark:text-sky-400"
												: (aggregateStats?.avgDividendGrowth || 0) < 0
													? "text-red-600 dark:text-red-400"
													: "text-sky-600/60 dark:text-sky-400/60",
										)}
									>
										{(aggregateStats?.avgDividendGrowth || 0).toFixed(2)}%
									</CardTitle>
								</CardHeader>
							</Card>
							<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
								<CardHeader className="pb-2">
									<CardDescription>Dividends Received</CardDescription>
									<CardTitle className="text-2xl text-sky-600 dark:text-sky-400">
										{dividendSummaryLoading ? (
											<Skeleton className="h-8 w-28" />
										) : (
											format(allPortfoliosDividendsReceived, { skipConversion: true })
										)}
									</CardTitle>
								</CardHeader>
							</Card>
						</div>
					)}

					<div className="lg:hidden">{aggregatePortfolioSummaryCard}</div>

					<FireSummaryWidget annualIncome={aggregateStats?.annualIncome || 0} />

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
						<div className="lg:col-span-2 space-y-6">
							<div className="flex items-center justify-between">
								<h2 className="text-2xl font-bold text-sky-950 dark:text-sky-50">My Honeycombs</h2>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{hydratedPortfolios.map((p) => (
									<Card
										key={p.id}
										className="cursor-pointer hover:shadow-md transition-shadow bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10 group"
										onClick={() => {
											navigate(`/dashboard/${p.id}`)
											Analytics.capture("Portfolio Selected")
										}}
									>
										<CardHeader className="pb-4">
											<div className="flex justify-between items-start">
												<div>
													<CardTitle className="text-xl group-hover:text-sky-600 transition-colors">
														{p.name}
													</CardTitle>
													<CardDescription>
														{p.hydratedPositions.length} Positions
													</CardDescription>
												</div>
												<div className="bg-sky-100 dark:bg-sky-900/50 p-2 rounded-lg">
													<LayoutGrid className="h-5 w-5 text-sky-600" />
												</div>
											</div>
										</CardHeader>
										<CardContent>
											{(() => {
												const pCash = cashByPortfolio.get(p.id)
												const cashConverted = pCash
													? convert(pCash.balance, pCash.currency)
													: null
												const displayValue = p.stats.totalValue + (cashConverted ?? 0)
												return (
													<div className="flex justify-between items-end">
														<div>
															<p className="text-sm text-muted-foreground">Value</p>
															<p className="text-xl font-bold text-sky-950 dark:text-sky-50">
																{format(displayValue, { skipConversion: true })}
															</p>
															{cashConverted != null && cashConverted > 0 && (
																<p className="text-xs text-muted-foreground mt-0.5">
																	incl.{" "}
																	{format(cashConverted, { skipConversion: true })}{" "}
																	cash
																</p>
															)}
														</div>
														<div className="text-right">
															<p className="text-sm text-muted-foreground">Yield</p>
															<p className="text-lg font-semibold text-sky-600">
																{(p.stats.portfolioYield || 0).toFixed(2)}%
															</p>
														</div>
													</div>
												)
											})()}
										</CardContent>
									</Card>
								))}
								<CreatePortfolioDialog onSuccess={fetchPortfolios} />
							</div>
						</div>

						<div className="lg:col-span-1 space-y-6">
							<h2 className="text-2xl font-bold text-sky-950 dark:text-sky-50">Diversification</h2>
							<div className="h-[400px]">
								<PortfolioPieChart positions={aggregateStats?.allPositions || []} />
							</div>
						</div>
					</div>

					<PerformanceSection />

					<div className="mt-8 space-y-3">
						<DividendBarChart portfolioIds={allPortfolioIds} activeTickers={allActiveTickers} />
						<div className="flex justify-end">
							<Link to="/calendar">
								<Button variant="outline" size="sm" className="gap-2 text-sky-900 dark:text-sky-100">
									<CalendarDays className="h-4 w-4" />
									View Calendar
								</Button>
							</Link>
						</div>
					</div>
				</div>

				{/* Desktop sidebar */}
				<aside className="hidden lg:block w-80 shrink-0">
					<div className="sticky top-20 space-y-4 pb-4 flex flex-col max-h-[calc(100vh-10rem)]">
						<div className="shrink-0">{aggregatePortfolioSummaryCard}</div>
						<div className="flex-1 min-h-0 overflow-y-auto">
							<ActivityFeed portfolioId={selectedPortfolioId ?? undefined} />
						</div>
					</div>
				</aside>
			</div>

			{/* Mobile feed trigger */}
			<div className="lg:hidden fixed bottom-6 right-6 z-50">
				<Sheet open={feedOpen} onOpenChange={setFeedOpen}>
					<SheetTrigger asChild>
						<Button
							size="icon"
							className="rounded-full h-12 w-12 shadow-lg bg-sky-600 hover:bg-sky-700 text-white"
						>
							<Activity className="h-5 w-5" />
						</Button>
					</SheetTrigger>
					<SheetContent className="w-80 p-0 overflow-y-auto">
						<ActivityFeed portfolioId={selectedPortfolioId ?? undefined} />
					</SheetContent>
				</Sheet>
			</div>
		</div>
	)
}
